import React from "react";

// Every typography direction has both a machine key (what the LogoIn schema
// accepts) and a rendered CSS example so the founder can eyeball each option
// before picking. The `sample` shows the brand name in a font stack
// approximating the direction, plus a short human description underneath.
export const TYPOGRAPHY_OPTIONS = [
  {
    value: "modern_grotesk",
    label: "Modern grotesk",
    description: "Inter · Söhne — crisp, functional, editorial-tech.",
    sampleClass: "font-sans font-semibold tracking-tight",
    style: { letterSpacing: "-0.02em" },
  },
  {
    value: "editorial_serif",
    label: "Editorial serif",
    description: "Freight · Canela — magazine premium, considered.",
    sampleClass: "font-serif italic",
    style: { fontFamily: 'Georgia, "Playfair Display", serif' },
  },
  {
    value: "hand_drawn",
    label: "Hand-drawn / marker",
    description: "Organic, imperfect, personable.",
    sampleClass: "font-semibold",
    style: { fontFamily: '"Caveat", "Kalam", cursive', letterSpacing: "0.02em" },
  },
  {
    value: "vintage_script",
    label: "Vintage script",
    description: "Retro Americana, hand-signed heritage.",
    sampleClass: "italic",
    style: { fontFamily: '"Homemade Apple", "Dancing Script", cursive', fontWeight: 500 },
  },
  {
    value: "all_caps_industrial",
    label: "All-caps industrial",
    description: "Heavy, blocky, monolithic, workwear.",
    sampleClass: "uppercase font-black tracking-wider",
    style: { fontFamily: '"Impact", "Oswald", sans-serif' },
  },
  {
    value: "custom_wordmark",
    label: "Custom wordmark",
    description: "Bespoke letterforms designed only for this brand.",
    sampleClass: "font-semibold uppercase tracking-widest",
    style: { fontFamily: 'ui-monospace, "Fira Code", monospace' },
  },
  {
    value: "condensed_display",
    label: "Condensed display",
    description: "Tall, narrow, dramatic magazine-cover.",
    sampleClass: "uppercase font-black",
    style: { fontFamily: '"Bebas Neue", "Oswald", "Impact", sans-serif',
             transform: "scaleY(1.25)", transformOrigin: "left", letterSpacing: "0.02em" },
  },
  {
    value: "rounded_friendly",
    label: "Rounded friendly",
    description: "Soft geometric — wellness, kids, playful.",
    sampleClass: "font-bold",
    style: { fontFamily: '"Nunito", "Quicksand", sans-serif' },
  },
  {
    value: "art_deco",
    label: "Art Deco",
    description: "1920s luxe, geometric, symmetric, gold-foil.",
    sampleClass: "uppercase tracking-[0.25em]",
    style: { fontFamily: '"Poiret One", "Cinzel", serif', fontWeight: 400 },
  },
  {
    value: "neo_bauhaus",
    label: "Neo-Bauhaus",
    description: "Circles + rectangles, primary color energy.",
    sampleClass: "uppercase font-black tracking-tight",
    style: { fontFamily: '"Archivo Black", "Space Grotesk", sans-serif' },
  },
  {
    value: "retro_70s",
    label: "Retro 70s bubble",
    description: "Groovy, warm palette, funk revival.",
    sampleClass: "font-black italic",
    style: { fontFamily: '"Fugaz One", "Rubik Bubbles", sans-serif',
             letterSpacing: "-0.02em" },
  },
  {
    value: "y2k_futuristic",
    label: "Y2K futuristic",
    description: "Glossy chrome, sci-fi, energy drink.",
    sampleClass: "uppercase font-black tracking-widest",
    style: { fontFamily: '"Orbitron", "Michroma", sans-serif' },
  },
  {
    value: "monospace_tech",
    label: "Monospace tech",
    description: "Code-editor precision, dev-tool positioning.",
    sampleClass: "font-semibold",
    style: { fontFamily: '"JetBrains Mono", "Fira Code", ui-monospace, monospace' },
  },
  {
    value: "luxury_serif",
    label: "Luxury high-contrast serif",
    description: "Vogue-style thin/thick — fashion, prestige.",
    sampleClass: "tracking-widest",
    style: { fontFamily: '"Didot", "Playfair Display", serif', fontWeight: 300 },
  },
  {
    value: "stencil_stamped",
    label: "Stencil / stamped",
    description: "DIY warehouse, small-batch craft feel.",
    sampleClass: "uppercase font-black tracking-widest",
    style: { fontFamily: '"Stencil Std", "Allerta Stencil", "Impact", sans-serif' },
  },
];

/**
 * Visual dropdown for choosing a logo typography direction. Each row renders
 * the brand name in the font style it will produce so the founder can eyeball
 * the difference before committing.
 */
export default function TypographyPicker({ value, onChange, brandNamePreview, testid }) {
  const [open, setOpen] = React.useState(false);
  const current = TYPOGRAPHY_OPTIONS.find((o) => o.value === value) || TYPOGRAPHY_OPTIONS[0];
  const sample = brandNamePreview?.trim() || "Warren's Brew";
  const rootRef = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={rootRef} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-trigger`}
        className="w-full text-left rounded-lg border border-shell-border bg-white px-3 py-2.5 hover:border-primary focus:border-primary focus:outline-none flex items-center justify-between gap-3"
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            {current.label}
          </div>
          <div
            className={"truncate mt-0.5 " + current.sampleClass}
            style={{ ...current.style, fontSize: "1.15rem" }}
          >
            {sample}
          </div>
        </div>
        <span className="text-muted-foreground text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-[420px] overflow-auto rounded-xl border border-shell-border bg-white shadow-xl"
             data-testid={`${testid}-menu`}>
          {TYPOGRAPHY_OPTIONS.map((opt) => {
            const selected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(opt.value); setOpen(false); }}
                data-testid={`${testid}-option-${opt.value}`}
                className={[
                  "w-full text-left px-3 py-2.5 border-b border-shell-border/50 last:border-b-0 hover:bg-muted transition",
                  selected ? "bg-primary/5" : "",
                ].join(" ")}
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold">
                  {selected && <span className="text-primary">●</span>}
                  {opt.label}
                </div>
                <div
                  className={"mt-1 " + opt.sampleClass}
                  style={{ ...opt.style, fontSize: "1.4rem", color: "#1f1a17" }}
                >
                  {sample}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {opt.description}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
